import { apiExchangeImpersonationTicket } from "./auth.api";

/**
 * Cache d'échange de ticket d'impersonation, au niveau module (donc partagé
 * par tous les montages de composants de cette page).
 *
 * Pourquoi ce cache existe :
 * `ImpersonateForm` peut être démonté puis remonté en plein vol, sans action
 * de l'utilisateur. `src/routes/index.tsx` choisit l'ENSEMBLE des routes en
 * fonction de la présence d'un token (`publicRoutes` vs `protectedRoutes`), et
 * l'app n'utilise pas de `PersistGate` : redux-persist réhydrate donc APRÈS le
 * premier rendu. L'apparition (ou la disparition) du token en cours de route
 * fait basculer tout le jeu de routes et démonte la page d'impersonation
 * pendant que sa requête est encore en vol.
 *
 * Conséquences si l'on ne mutualise pas la promesse :
 *  - rappeler l'API au remontage → le ticket est à usage unique, le 2e appel
 *    échoue en 401 et on affiche « lien invalide » alors que le 1er a réussi ;
 *  - bloquer le remontage par un simple drapeau « déjà tenté » → le composant
 *    n'a plus aucune promesse à laquelle se raccrocher et reste bloqué sur
 *    « Connexion en cours… » indéfiniment.
 *
 * La bonne réponse est de réutiliser LA MÊME promesse : un remontage s'y
 * raccroche, obtient le même résultat (succès comme échec) et peut terminer le
 * travail, sans jamais consommer le ticket deux fois.
 */
const exchangesByTicket = new Map<string, Promise<any>>();

export const exchangeImpersonationTicketOnce = (
  ticket: string
): Promise<any> => {
  const existing = exchangesByTicket.get(ticket);

  if (existing) {
    return existing;
  }

  const request = apiExchangeImpersonationTicket(ticket);

  exchangesByTicket.set(ticket, request);

  return request;
};

/**
 * Indique si l'échange de ce ticket a déjà été lancé dans cette page.
 * Utilisé par `src/routes/index.tsx` pour distinguer « une session étrangère
 * traînait déjà avant qu'on arrive » (qu'il faut nettoyer) de « la session
 * vient d'être créée par notre propre échange » (qu'il ne faut surtout pas
 * effacer). Indépendant du timing de réhydratation de redux-persist.
 */
export const hasImpersonationExchangeStarted = (ticket: string): boolean =>
  exchangesByTicket.has(ticket);
